import React, { useState, useEffect } from 'react'
import { Grid, FormControl, InputLabel, Select, MenuItem } from '@material-ui/core'
import Button from '@material-ui/core/Button'
import DeleteIcon from '@material-ui/icons/Delete'

import { Dialog, snackbar } from 'components'
import CustomTextField from 'components/CustomTextField'
import { useApiCall } from 'hooks'
import { Tag } from 'features/Main/Tags/types'
import { CustomCheckbox } from 'components/CustomCheckbox'
import { CustomFormControlLabel } from 'components/CustomFormControlLabel'

import { UserPoll, UpdateUserPollDetailResponse, UserPollDetail } from '../../types'
import {
  getUserPollDetails,
  updateUserPollDetail,
  createUserPollDetail,
  deleteUserPollDetail,
} from '../../api'
import { useStyles } from './styles'
import { constants } from '../../constansts'

interface CreateUserPollDialogProps {
  handleClose: () => void
  userPoll: UserPoll
  tags: Tag[]
}

const MAX_ACTIVE_ANSWERS = 6

const UserPollDetailDialog: React.FC<CreateUserPollDialogProps> = ({
  handleClose,
  userPoll,
  tags,
}) => {
  const classes = useStyles()
  const [userPollDetails, setUserPollDetails] = useState(userPoll.detail)
  const [deletedUserPollDetails, setDeletedUserPollDetails] = useState<UserPollDetail[]>([])
  const [isSaving, setIsSaving] = useState(false)
  // const [icon, setIcon] = useState<File | null>(null)
  const [updateUserPollDetailApi] = useApiCall<
    {
      id: number
      userPollId: number
      active?: boolean
      answer?: string
      tag?: string
      icon?: File
    },
    UpdateUserPollDetailResponse
  >(updateUserPollDetail)
  const [createUserPollDetailApi] = useApiCall<
    {
      id: number
      userPollId: number
      active?: boolean
      answer?: string
      tag?: string
      icon?: File
    },
    UpdateUserPollDetailResponse
  >(createUserPollDetail)
  const [getUserPollDetailsApi, isLoading] = useApiCall<UserPoll, UserPollDetail[]>(
    getUserPollDetails,
  )
  const [deleteUserPollApi] = useApiCall<UserPollDetail, void>(deleteUserPollDetail)

  useEffect(() => {
    if (userPoll) {
      const fetchData = async () => {
        const userPollDetailsData = await getUserPollDetailsApi(userPoll)

        if (userPollDetailsData) setUserPollDetails(userPollDetailsData)
      }
      fetchData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleTitleChange = async (e: any, idx: number) => {
    if (e.target.value.length < 1) {
      snackbar.show(`Debe ingresar un texto`)
      return
    }

    const updated = userPollDetails.map((detail, index) =>
      index === idx ? { ...detail, answer: e.target.value } : detail,
    )
    setUserPollDetails(updated)
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleTagChange = (e: any, index: number) => {
    const updated = userPollDetails.map((detail, idx) =>
      idx === index ? { ...detail, tag: e.target.value } : detail,
    )
    setUserPollDetails(updated)
  }

  /* const handleIconChange = async (file: File, item: UserPollDetail) => {
    if (file.name.split('.').pop() != 'svg') {
      snackbar.show(`Sólo está permitido subir archivos con extension "svg"`)
      return
    }
    const imageUrl = URL.createObjectURL(file)
    const newUserPollDetail = userPollDetails.map(el => {
      if (item == el) {
        el.iconUrl = imageUrl
      }
      return el
    })

    setIcon(file)
    setUserPollDetails(newUserPollDetail)
  } */

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleChangeActive = (e: any, item: UserPollDetail, index: number) => {
    if (
      !item.active &&
      userPollDetails.filter(el => el.active === true).length === MAX_ACTIVE_ANSWERS
    ) {
      snackbar.show(
        `Sólo está permitido tener un máximo de ${MAX_ACTIVE_ANSWERS} respuestas activas`,
      )
      return
    }
    /* if (!item.iconUrl)
    {
      snackbar.show(`Debe tener una imagen para poder activar esta respuesta`)
      return
    } */
    if (!item.tag) {
      snackbar.show(`Debe seleccinar un tag para activar esta respuesta`)
      return
    }

    const updated = userPollDetails.map((detail, idx) =>
      idx === index ? { ...detail, active: !detail.active } : detail,
    )
    setUserPollDetails(updated)
  }

  const handleAddNewItem = () => {
    const existsEmpty = userPollDetails.find(el => el.answer === '')
    if (existsEmpty) {
      snackbar.show(`Tiene un elemento sin completar aún`)
      return
    }

    const newItem = {
      id: null,
      answer: '',
      iconUrl: '',
      active: null,
      tag: null,
      userPollId: userPoll.id,
    }
    setUserPollDetails([...userPollDetails, newItem])
  }

  const handleRemoveItem = async (item: UserPollDetail) => {
    const existing = userPollDetails.find(el => item.id === el.id)
    if (!existing) return

    if (existing.id) setDeletedUserPollDetails(prev => [...prev, existing])

    const newUserPollDetails = userPollDetails.filter(el => el !== item)
    setUserPollDetails(newUserPollDetails)
  }

  const handleIsChecked = (active: boolean | null | undefined) => {
    if (!active) return false
    return true
  }

  const isChangeTagDisabled = (item: UserPollDetail) => {
    if (!item.tag) return false

    return item.active === true
  }

  const handleSave = async () => {
    try {
      // Basic validations before persisting
      const hasEmptyAnswer = userPollDetails.some(d => !d.answer || d.answer.trim() === '')
      if (hasEmptyAnswer) {
        snackbar.show(`Debe ingresar un texto en todas las respuestas`)
        return
      }
      const activeCount = userPollDetails.filter(d => d.active === true).length
      if (activeCount > MAX_ACTIVE_ANSWERS) {
        snackbar.show(
          `Sólo está permitido tener un máximo de ${MAX_ACTIVE_ANSWERS} respuestas activas`,
        )
        return
      }
      const activeWithoutTag = userPollDetails.some(d => d.active && !d.tag)
      if (activeWithoutTag) {
        snackbar.show(`Debe seleccinar un tag para activar una respuesta`)
        return
      }

      setIsSaving(true)

      // Deletions first
      const deletions = deletedUserPollDetails.filter(d => !!d.id)
      if (deletions.length) {
        await Promise.all(deletions.map(d => deleteUserPollApi(d)))
      }

      // Updates and creations
      const toUpdate = userPollDetails.filter(d => !!d.id)
      const toCreate = userPollDetails.filter(d => !d.id)

      if (toUpdate.length) {
        await Promise.all(
          toUpdate.map(d =>
            updateUserPollDetailApi({
              id: (d.id as unknown) as number,
              userPollId: ((d.userPollId as unknown) as number) || userPoll.id,
              answer: d.answer,
              tag: d.tag ? d.tag.toString() : undefined,
              active: d.active === null ? undefined : (d.active as boolean),
              icon: d.icon,
            }),
          ),
        )
      }

      if (toCreate.length) {
        await Promise.all(
          toCreate.map(d =>
            createUserPollDetailApi({
              id: 0,
              userPollId: userPoll.id,
              answer: d.answer,
              tag: d.tag ? d.tag.toString() : undefined,
              active: d.active === null ? undefined : (d.active as boolean),
              icon: d.icon,
            }),
          ),
        )
      }

      // Refresh data to sync IDs/state
      const refreshed = await getUserPollDetailsApi(userPoll)
      if (refreshed) setUserPollDetails(refreshed)
      setDeletedUserPollDetails([])

      snackbar.show(`Respuestas guardadas`)
    } catch (e) {
      snackbar.show(`Ocurrió un error al guardar`)
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Dialog
      title={`${constants.TEST_NAME} respuestas`}
      isOpen
      showActions
      onCancel={handleClose}
      isLoading={isLoading || isSaving}
      contentStyle={{ minWidth: 800 }}
      style={{ minWidth: '80%' }}
      okButtonText="Guardar"
      onAccept={handleSave}
    >
      <Grid container spacing={3}>
        {userPollDetails &&
          userPollDetails.map((el, index) => (
            <React.Fragment key={el.id ?? `new-${index}`}>
              <Grid item lg={6} xs={6}>
                <CustomTextField
                  style={{ paddingTop: '4px' }}
                  label="Respuesta"
                  value={el.answer}
                  fullWidth
                  onChange={ev => handleTitleChange(ev, index)}
                  disabled={isLoading}
                  id={`${el.id}`}
                  maxLength={30}
                />
              </Grid>
              <Grid item xs={3} sm={3}>
                <InputLabel className={classes.multiselectTitle}>Tag</InputLabel>
                <FormControl className={classes.fullWidth}>
                  <Select
                    labelId="demo-simple-select-label"
                    id={`demo-simple-select-${el.id}`}
                    value={el.tag}
                    onChange={ev => handleTagChange(ev, index)}
                    disabled={isChangeTagDisabled(el)}
                  >
                    {tags.map(tag => (
                      <MenuItem value={tag.id}>
                        {tag.id} - {tag.title}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              {/*          <Grid item xs={2} sm={2}>
            <div className={classes.iconUploadContainer}>
              <UploadButton
                id={`userPoll-icon-upload-button-${el.id}`}
                accept=".jpg, .jpeg, .png"
                label="Subir ícono"
                options={{userPollDetailsId: el.id}}
                onChange={(ev) => handleIconChange(ev, el)}
              />
              <div className={classes.iconUploadPreviewContainer}>
                <img src={el.iconUrl} alt="" className={classes.iconPreview} />
              </div>
            </div>
          </Grid> */}
              <Grid
                item
                xs={2}
                sm={2}
                style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}
              >
                <CustomFormControlLabel
                  className={classes.checkbox}
                  control={
                    <CustomCheckbox
                      onChange={ev => handleChangeActive(ev, el, index)}
                      checked={handleIsChecked(el.active)}
                    />
                  }
                  label="Activar"
                />
              </Grid>
              <Grid
                item
                xs={1}
                sm={1}
                style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}
              >
                <Button onClick={() => handleRemoveItem(el)}>
                  <DeleteIcon />
                </Button>
              </Grid>
            </React.Fragment>
          ))}
        <Grid
          item
          xs={12}
          sm={12}
          style={{ position: 'absolute', bottom: 0, paddingBottom: '30px' }}
        >
          <Button
            onClick={handleAddNewItem}
            variant="contained"
            color="secondary"
            autoFocus
            style={{
              borderRadius: '10px',
              fontSize: '14px',
              fontWeight: 400,
              fontFamily: 'Montserrat',
              lineHeight: '100%',
              letterSpacing: 0,
              minWidth: '110px',
              minHeight: '50px',
              textTransform: 'uppercase',
            }}
          >
            Agregar respuesta
          </Button>
        </Grid>
      </Grid>
    </Dialog>
  )
}

export { UserPollDetailDialog }
